import {useContext, useEffect, useRef, useState} from "preact/hooks";
import {MusicalNoteIcon} from "@heroicons/react/24/solid";
import API_URL from "../includes/api.js";
import ytReady from "../includes/youtube.js";
import {useAuth} from "./AuthProvider.jsx";
import {NotificationContext} from "./NotificationContext.jsx";

const HEARTBEAT_MS = 15000;

export function EventPlayer({submissionId, mediaType}) {
    const {getToken} = useAuth();
    const {addNotification} = useContext(NotificationContext);

    const [source, setSource] = useState(null);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);

    const mediaRef = useRef(null);
    const ytContainerRef = useRef(null); // DOM node the YT iframe replaces
    const ytPlayerRef = useRef(null);    // YT.Player instance (youtube entries)
    const retriedRef = useRef(false);
    const resumeTimeRef = useRef(0);
    const autoPlayRef = useRef(false);

    // View-session reporting. The play endpoint returns view_id: null when the
    // caller owns the submission — in that case ALL progress reporting below
    // is skipped (owners never create view records).
    const viewIdRef = useRef(null);
    const tokenRef = useRef(null);        // cached for sendBeacon (sync-only context)
    const maxTimeRef = useRef(0);         // max playhead reached this session
    const lastPostedRef = useRef(0);      // last seconds value the API acknowledged
    const reportingStoppedRef = useRef(false);
    const heartbeatRef = useRef(null);

    const fetchSource = async (silent = false) => {
        const token = await getToken();
        if (!token) return null;
        tokenRef.current = token;
        try {
            let url = `${API_URL}/event/play?s=${submissionId}&firebase_auth_token=${token}`;
            // Echo the current view_id on retry-on-expiry so the API reuses the
            // same view session instead of counting a second playback.
            if (viewIdRef.current !== null) url += `&view_id=${viewIdRef.current}`;
            const response = await fetch(url);
            const data = await response.json();
            if (data.code === 200) {
                viewIdRef.current = data.data.view_id ?? null;
                return data.data;
            }
            if (!silent) addNotification(data.message, 'error');
        } catch (error) {
            if (!silent) addNotification('Could not load this entry.', 'error');
        }
        return null;
    };

    const captureMaxTime = () => {
        let currentTime = null;
        const yt = ytPlayerRef.current;
        if (yt && typeof yt.getCurrentTime === "function") {
            currentTime = yt.getCurrentTime();
        } else if (mediaRef.current) {
            currentTime = mediaRef.current.currentTime;
        }
        if (Number.isFinite(currentTime)) {
            maxTimeRef.current = Math.max(maxTimeRef.current, currentTime);
        }
        return Math.floor(maxTimeRef.current);
    };

    const stopHeartbeat = () => {
        if (heartbeatRef.current !== null) {
            clearInterval(heartbeatRef.current);
            heartbeatRef.current = null;
        }
    };

    // Reports the max playhead reached so far. Posts only when the value has
    // grown since the last acknowledged post. The API stores a monotonic max
    // and answers repeated values with 200; a 404 means the view session no
    // longer exists, so reporting stops for good. Anything else (rate limit,
    // 5xx) is transient — the next heartbeat retries.
    const postProgress = async () => {
        if (viewIdRef.current === null || reportingStoppedRef.current) return;
        const seconds = captureMaxTime();
        if (seconds <= lastPostedRef.current) return;
        const token = await getToken();
        if (!token) return;
        tokenRef.current = token;
        const formData = new FormData();
        formData.append('firebase_auth_token', token);
        formData.append('view_id', viewIdRef.current);
        formData.append('seconds', seconds);
        try {
            const response = await fetch(`${API_URL}/event/view_progress`, {
                method: 'POST',
                body: formData
            });
            const data = await response.json();
            if (data.code === 200) {
                lastPostedRef.current = Math.max(lastPostedRef.current, seconds);
            } else if (data.code === 404) {
                reportingStoppedRef.current = true;
                stopHeartbeat();
            }
        } catch (error) {
            // Transient network failure — the next heartbeat retries.
        }
    };

    // Last-chance report when the page is hidden or the player unmounts.
    // sendBeacon with FormData needs no CORS preflight and survives unload;
    // it uses the cached token because this context cannot await a fresh one.
    const beaconProgress = () => {
        if (viewIdRef.current === null || reportingStoppedRef.current) return;
        const seconds = captureMaxTime();
        if (seconds <= lastPostedRef.current) return;
        if (!tokenRef.current || typeof navigator.sendBeacon !== "function") return;
        const formData = new FormData();
        formData.append('firebase_auth_token', tokenRef.current);
        formData.append('view_id', viewIdRef.current);
        formData.append('seconds', seconds);
        if (navigator.sendBeacon(`${API_URL}/event/view_progress`, formData)) {
            lastPostedRef.current = seconds;
        }
    };

    const handlePlay = () => {
        if (viewIdRef.current === null || reportingStoppedRef.current) return;
        stopHeartbeat();
        heartbeatRef.current = setInterval(postProgress, HEARTBEAT_MS);
    };

    // Also handles "ended": stop the heartbeat and flush progress immediately.
    const handlePause = () => {
        stopHeartbeat();
        postProgress();
    };

    // YouTube entries: mount a youtube-nocookie embed through the IFrame API
    // so getCurrentTime() can feed the same heartbeat lifecycle the native
    // players use. Defined before the fetch effect below so that on unmount
    // this cleanup runs first — it captures the final playhead before
    // destroying the player, and the fetch effect's cleanup then beacons it.
    useEffect(() => {
        if (!source || source.source_type !== "youtube" || !source.youtube_id) return;
        let player = null;
        let cancelled = false;
        ytReady.then((YT) => {
            if (cancelled || !ytContainerRef.current) return;
            player = new YT.Player(ytContainerRef.current, {
                videoId: source.youtube_id,
                width: '100%',
                height: '100%',
                host: 'https://www.youtube-nocookie.com',
                playerVars: {
                    playsinline: 1,
                    enablejsapi: 1,
                    rel: 0,
                    origin: window.location.origin
                },
                events: {
                    onStateChange: (e) => {
                        if (e.data === YT.PlayerState.PLAYING) {
                            handlePlay();
                        } else if (e.data === YT.PlayerState.PAUSED || e.data === YT.PlayerState.ENDED) {
                            handlePause();
                        }
                    }
                }
            });
            ytPlayerRef.current = player;
        });
        return () => {
            cancelled = true;
            captureMaxTime();
            ytPlayerRef.current = null;
            if (player) player.destroy();
        };
    }, [source]);

    useEffect(() => {
        let cancelled = false;
        retriedRef.current = false;
        resumeTimeRef.current = 0;
        autoPlayRef.current = false;
        viewIdRef.current = null;
        maxTimeRef.current = 0;
        lastPostedRef.current = 0;
        reportingStoppedRef.current = false;
        setSource(null);
        setFailed(false);
        setLoading(true);
        fetchSource().then(data => {
            if (cancelled) return;
            if (data) {
                setSource(data);
            } else {
                setFailed(true);
            }
            setLoading(false);
        });
        const handleVisibility = () => {
            if (document.visibilityState === "hidden") beaconProgress();
        };
        document.addEventListener("visibilitychange", handleVisibility);
        return () => {
            cancelled = true;
            document.removeEventListener("visibilitychange", handleVisibility);
            stopHeartbeat();
            beaconProgress();
        };
    }, [submissionId]);

    // Presigned URLs expire mid-session; retry once with a fresh URL and
    // resume at the position where playback stopped.
    const handleError = async () => {
        if (retriedRef.current) {
            setFailed(true);
            return;
        }
        retriedRef.current = true;
        const media = mediaRef.current;
        resumeTimeRef.current = media && Number.isFinite(media.currentTime) ? media.currentTime : 0;
        autoPlayRef.current = true;
        const data = await fetchSource(true);
        if (data) {
            setSource(data);
        } else {
            setFailed(true);
        }
    };

    const handleLoadedMetadata = () => {
        const media = mediaRef.current;
        if (!media) return;
        if (resumeTimeRef.current > 0) {
            media.currentTime = resumeTimeRef.current;
            resumeTimeRef.current = 0;
        }
        if (autoPlayRef.current) {
            autoPlayRef.current = false;
            media.play().catch(() => {});
        }
    };

    if (loading) {
        return <div className="text-gray-400 text-sm py-4 text-center">Loading player...</div>;
    }

    // A youtube source without a youtube_id can never mount a player (the
    // effect above requires both), so treat it as unplayable rather than
    // rendering a permanently empty black container.
    if (failed || !source || (source.source_type === "youtube" && !source.youtube_id)) {
        return <div className="text-gray-400 text-sm py-4 text-center">This entry cannot be played right now.</div>;
    }

    // YouTube entries embed the video instead of streaming from S3; there is
    // no presigned URL to expire, so the retry-on-error path does not apply.
    if (source.source_type === "youtube") {
        return (
            <div className="w-full aspect-video rounded-lg overflow-hidden bg-black">
                <div ref={ytContainerRef} className="w-full h-full"/>
            </div>
        );
    }

    const resolvedType = source.media_type || mediaType;

    if (resolvedType === "video") {
        return (
            <video
                ref={mediaRef}
                controls
                playsInline
                src={source.url}
                onError={handleError}
                onLoadedMetadata={handleLoadedMetadata}
                onPlay={handlePlay}
                onPause={handlePause}
                onEnded={handlePause}
                onTimeUpdate={captureMaxTime}
                className="w-full rounded-lg bg-black aspect-video"
            />
        );
    }

    return (
        <div>
            <div className="w-full aspect-video rounded-lg bg-gradient-to-br from-zinc-700 via-zinc-800 to-black flex items-center justify-center mb-2">
                <MusicalNoteIcon className="w-16 h-16 text-white/30" aria-hidden="true"/>
            </div>
            <audio
                ref={mediaRef}
                controls
                src={source.url}
                onError={handleError}
                onLoadedMetadata={handleLoadedMetadata}
                onPlay={handlePlay}
                onPause={handlePause}
                onEnded={handlePause}
                onTimeUpdate={captureMaxTime}
                className="w-full"
            />
        </div>
    );
}
